import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  doc, onSnapshot, getDoc, updateDoc, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import TopBar from '../components/TopBar';
import useUserPrefs from '../hooks/useUserPrefs';
import { isFirmwareUpdating } from '../config/firmware';
import {
  validateCodes, calPointsFromCodes,
  validateTankInfo,
  deriveMaFromCalChange,
  regress, maToFeet,
  validateRegressionPlausibility,
} from '../utils/cal';

// ── Constants ──────────────────────────────────────────────────────────────

// Fast-mode durations for cloud→device cadence bump while user is actively
// in the wizard. The cloud function caps server-side at 30 min, so these
// can't exceed that.
const FAST_MODE_MANUAL_CAL_MS = 10 * 60 * 1000;  // 10 min during manual cal
// FIX-3 (session 27): this was missing — line 487 referenced it and threw
// ReferenceError on every wizard save. 2 min matches FAST_MODE_SETTINGS_EDIT_MS
// in Settings.js; well under the cloud's 30-min server-side cap.
const FAST_MODE_SAVE_MS = 2 * 60 * 1000;         // 2 min after a wizard save

// Minimum captures for manual cal before user can finish.
const MANUAL_CAL_MIN_POINTS = 2;
const MANUAL_CAL_MAX_POINTS = 8;

// Minimum span between manual cal depths to fit a reliable line.
const MANUAL_CAL_MIN_SPAN_FT = 1.0;

// Capture settle window. After the user clicks Capture, the button shows
// a countdown for this many seconds while the device pushes a fresh
// reading and the sensor settles physically. The captured mA is the
// `liveMA` at the moment the countdown ends, NOT at click time.
//
// 10 seconds at 5s push cadence guarantees at least one fresh push lands
// during the window, plus ~5s of buffer for sensor physical settle and
// network propagation.
const CAPTURE_SETTLE_MS = 10 * 1000;

// Freshness threshold for the live sensor reading display. Below this,
// the reading is shown as fresh (subdued styling); above this, amber
// "waiting for refresh" styling.
const READING_STALE_THRESHOLD_MS = 7 * 1000;

// ── Depth format helpers ───────────────────────────────────────────────────

function formatDepth(depthFt, format) {
  if (typeof depthFt !== 'number' || !isFinite(depthFt)) return '—';
  if (format === 'inches') {
    return `${Math.round(depthFt * 12)} in`;
  }
  if (format === 'ft-decimal') {
    return `${depthFt.toFixed(1)} ft`;
  }
  const totalInches = depthFt * 12;
  const ft = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches - ft * 12);
  if (inches === 12) return `${ft + 1} ft 0 in`;
  return `${ft} ft ${inches} in`;
}

// ════════════════════════════════════════════════════════════════════════════
//  Main component
// ════════════════════════════════════════════════════════════════════════════

function WizCheck() { return (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l4 4 10-10" /></svg>); }
function WizWarn() { return (<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>); }
function WizBolt() { return (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" /></svg>); }
function WizDrop() { return (<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3s-6 6.7-6 11a6 6 0 0 0 12 0c0-4.3-6-11-6-11Z" /></svg>); }
function WizHelp() { return (<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 4.5 1.5c-.7.7-1.5 1.2-1.5 2.5" /><circle cx="12" cy="17" r=".6" fill="currentColor" /></svg>); }
function WizChevron() { return (<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>); }

export default function CalibrationWizard({ user }) {
  const { deviceId, tank: tankParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { prefs } = useUserPrefs(user);

  // tank: 1 or 2
  const [tank, setTank] = useState(tankParam ? Number(tankParam) : 1);

  // Steps:
  //   0 — tank picker (only if 2 tanks and no URL param)
  //   1 — choose branch (factory tag OR manual cal)
  //   2 — factory tag entry (Branch A) — codeA, codeB
  //   3 — manual cal capture (Branch B) — list of (depthFt, mA) points
  //   4 — tank info (empty/full feet, capacity)
  //   5 — confirm + save
  const [step, setStep] = useState(0);

  // Branch: 'factory' or 'manual', set at step 1.
  const [branch, setBranch] = useState(null);

  // True when onboarding a device that already carries factory
  // calibration — we skip straight to the tank-levels step (4) and
  // hide the "back" affordance since there are no prior steps.
  const [factorySkip, setFactorySkip] = useState(false);

  // Set when the user taps "Restore factory calibration" — the archived
  // factory calPoints to write on save (routed through the same path as a
  // fresh cal).
  const [restoredCalPoints, setRestoredCalPoints] = useState(null);

  // Live device state
  const [reading, setReading]       = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading]       = useState(true);

  // Branch A — factory tag inputs (Code A at 1 ft, Code B at 4 ft)
  const [codeA, setCodeA]   = useState('');
  const [codeB, setCodeB]   = useState('');
  const [, setCodeError] = useState('');

  // Branch B — manual cal captures: array of { depthFt, mA, ts }
  const [manualCaptures, setManualCaptures] = useState([]);

  // Capturing state — true while the 10s settle countdown is running.
  // Disables the Capture button, replaces label with countdown timer,
  // and on completion captures liveMA paired with the typed depth.
  const [capturing, setCapturing] = useState(false);
  const [captureRemainingMs, setCaptureRemainingMs] = useState(0);

  // Captured-flash state — briefly shows "Captured ✓" after a successful
  // capture before the button returns to its idle state. Pure cosmetic
  // confirmation; doesn't gate anything.
  const [justCaptured, setJustCaptured] = useState(false);

  // Slope-plausibility error from the most recent save attempt. Cleared
  // on any capture or capture-removal so the user can retry cleanly.
  const [plausibilityErr, setPlausibilityErr] = useState('');
  // While at capture step, user enters a depth then presses Ready to lock in mA
  const [pendingDepth, setPendingDepth] = useState('');  // used by ft-decimal and inches modes
  const [pendingFt, setPendingFt]       = useState('');  // used by ft-in mode (feet half)
  const [pendingIn, setPendingIn]       = useState('');  // used by ft-in mode (inches half)
  const [pendingErr, setPendingErr]     = useState('');

  // Tank info inputs (canonical: decimal feet strings)
  const [emptyDepth, setEmptyDepth] = useState('');
  const [fullDepth, setFullDepth]   = useState('');
  // ft-in mode: separate ft + in inputs
  const [emptyFt, setEmptyFt]       = useState('');
  const [emptyIn, setEmptyIn]       = useState('');
  const [fullFt, setFullFt]         = useState('');
  const [fullIn, setFullIn]         = useState('');
  const [capacity, setCapacity]     = useState('');

  const [saving, setSaving]     = useState(false);
  const [saveError, setSaveError] = useState('');

  // Where to navigate back to on cancel/save
  const returnTo = location.state?.returnTo || '/dashboard';
  // Onboarding (first-time setup from ClaimDevice) vs recalibration
  // (from Settings/Dashboard).
  const onboarding = location.state?.onboarding === true;
  // Explicit "Recalibrate" intent (from the Settings Recalibrate button).
  // Forces the calibration step even when the tank is already calibrated;
  // everything else skips straight to tank info when a tank already has cal.
  const recalibrate = location.state?.recalibrate === true;

  // ── Load device + claim docs ─────────────────────────────────────────────
  useEffect(() => {
    if (!user || !deviceId) return;
    let cancelled = false;

    async function loadInitial() {
      try {
        const [devSnap, claimSnap] = await Promise.all([
          getDoc(doc(db, 'devices', deviceId)),
          getDoc(doc(db, 'users', user.uid, 'devices', deviceId)),
        ]);
        if (cancelled) return;
        if (!claimSnap.exists()) {
          // User doesn't have a claim on this device — bounce home
          navigate('/dashboard', { replace: true });
          return;
        }

        const dev = devSnap.exists() ? devSnap.data() : {};
        setDeviceData(dev);

        // Don't seed here — done in a dedicated effect below that
        // depends on deviceData + tank + prefs.depthFormat. This
        // avoids race conditions where the initial render's prefs
        // value (default) doesn't match the user's actual pref by
        // the time async loads complete.

        // Decide initial step.
        // If the tank we're setting up ALREADY has calibration (factory-
        // provisioned, or a previous manual cal) and the user didn't
        // explicitly ask to Recalibrate, skip the sensor-calibration steps
        // entirely and land on tank info (step 4). The sensor is already
        // calibrated; we only need the tank's empty/full/capacity. Only show
        // the calibration steps when the tank is uncalibrated OR the user
        // pressed "Recalibrate".
        const decisionTank = tankParam ? Number(tankParam) : 1;
        const decisionCal  = dev[`tank${decisionTank}`]?.calPoints;
        const decisionCalibrated = Array.isArray(decisionCal) && decisionCal.length >= 2;
        if (!recalibrate && decisionCalibrated) {
          setTank(decisionTank);
          setFactorySkip(true);
          setStep(4);
        } else if (dev.lastReading?.s2 && !tankParam) {
          setStep(0);
        } else {
          setStep(1);
        }

        setLoading(false);
      } catch (err) {
        console.error('Wizard load error:', err);
        if (!cancelled) setLoading(false);
      }
    }
    loadInitial();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, deviceId, tank, tankParam, navigate]);

  // Seed tank info inputs from deviceData whenever it changes (or
  // when the depth format pref changes). Re-runs cleanly if either
  // arrives async. Only seeds when fields are currently blank — if
  // user has typed something, don't overwrite their input.
  useEffect(() => {
    if (!deviceData) return;
    const t = deviceData[`tank${tank}`] || {};
    if (typeof t.emptyFt === 'number') {
      seedDepth('empty', t.emptyFt);
    }
    if (typeof t.fullFt === 'number') {
      seedDepth('full', t.fullFt);
    }
    if (typeof t.capacity === 'number' && t.capacity > 0) {
      setCapacity(prev => prev || String(Math.round(t.capacity)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceData, tank, prefs.depthFormat]);

  // Live subscription to readings
  useEffect(() => {
    if (!deviceId) return;
    const unsub = onSnapshot(
      doc(db, 'devices', deviceId),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setReading(data.lastReading || null);
        }
      },
      (err) => console.error('Wizard snapshot error:', err)
    );
    return unsub;
  }, [deviceId]);

  // Seed depth inputs from a decimal-feet value, respecting the user's
  // depth-format preference.
  function seedDepth(which, depthFt) {
    if (prefs.depthFormat === 'ft-in') {
      const totalIn = depthFt * 12;
      const ft = Math.floor(totalIn / 12);
      const inches = Math.round(totalIn - ft * 12);
      if (which === 'empty') { setEmptyFt(String(ft)); setEmptyIn(String(inches)); }
      else                   { setFullFt(String(ft));  setFullIn(String(inches));  }
    } else if (prefs.depthFormat === 'inches') {
      const totalIn = Math.round(depthFt * 12);
      if (which === 'empty') setEmptyDepth(String(totalIn));
      else                   setFullDepth(String(totalIn));
    } else {
      // ft-decimal
      if (which === 'empty') setEmptyDepth(depthFt.toFixed(1));
      else                   setFullDepth(depthFt.toFixed(1));
    }
  }

  // Canonical depth-in-feet from input state
  const emptyDepthFt = useMemo(() => parseInputDepth(
    prefs.depthFormat, emptyDepth, emptyFt, emptyIn
  ), [prefs.depthFormat, emptyDepth, emptyFt, emptyIn]);

  const fullDepthFt = useMemo(() => parseInputDepth(
    prefs.depthFormat, fullDepth, fullFt, fullIn
  ), [prefs.depthFormat, fullDepth, fullFt, fullIn]);

  // Blank vs. explicit zero: parseInputDepth returns 0 for both, so
  // "did the user type anything" must come from the raw input strings.
  // An entered 0 ft 0 in is a legitimate empty depth and must save as 0,
  // not null.
  const emptyEntered = prefs.depthFormat === 'ft-in'
    ? (emptyFt !== '' || emptyIn !== '')
    : emptyDepth !== '';
  const fullEntered = prefs.depthFormat === 'ft-in'
    ? (fullFt !== '' || fullIn !== '')
    : fullDepth !== '';

  // ── Live sensor state ───────────────────────────────────────────────────
  const liveMA = reading ? (tank === 1 ? reading.mA1 : reading.mA2) : null;

  // Timestamp of the last reading, as JS millis. Firestore Timestamp
  // has toMillis(); a raw server-timestamp object during write may not
  // have it set yet. Default to null when unavailable.
  const lastReadingMs = reading?.timestamp?.toMillis?.() ?? null;

  // Live "Xs ago" counter — re-renders every second so the user sees
  // the reading age tick up. Cheap timer; only runs while step 3 is
  // mounted with the manual branch active.
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (step !== 3 || branch !== 'manual') return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [step, branch]);

  const readingAgeMs = lastReadingMs ? Math.max(0, nowMs - lastReadingMs) : null;
  const readingFresh = readingAgeMs != null && readingAgeMs <= READING_STALE_THRESHOLD_MS;

  // ── Manual cal: enter fast mode when stepping into capture ──────────────
  // Bumps device→cloud cadence to 15s so the captured mA on Ready-press
  // reflects roughly-current sensor reading.
  useEffect(() => {
    if (step !== 3 || branch !== 'manual') return;
    let cancelled = false;
    async function bumpFastMode() {
      try {
        await updateDoc(doc(db, 'devices', deviceId), {
          fastModeUntil: Timestamp.fromMillis(Date.now() + FAST_MODE_MANUAL_CAL_MS),
        });
      } catch (err) {
        if (!cancelled) console.warn('Fast mode bump failed:', err);
      }
    }
    bumpFastMode();
    return () => { cancelled = true; };
  }, [step, branch, deviceId]);

  // ── Validation ──────────────────────────────────────────────────────────

  // Branch A (factory tag): hard validation via cal.js
  const codeValidation = useMemo(
    () => validateCodes(codeA, codeB),
    [codeA, codeB]
  );

  // Branch B (manual cal): need ≥ 2 captures with sufficient span
  const manualCalReady = useMemo(() => {
    if (manualCaptures.length < MANUAL_CAL_MIN_POINTS) return false;
    const depths = manualCaptures.map(c => c.depthFt);
    const span = Math.max(...depths) - Math.min(...depths);
    return span >= MANUAL_CAL_MIN_SPAN_FT;
  }, [manualCaptures]);

  // Tank info validation
  const tankInfoValidation = useMemo(() => {
    return validateTankInfo(
      emptyEntered ? String(emptyDepthFt) : '',
      fullEntered  ? String(fullDepthFt)  : ''
    );
  }, [emptyEntered, fullEntered, emptyDepthFt, fullDepthFt]);

  const capacityValid = !capacity ||
    (parseFloat(capacity) >= 0 && parseFloat(capacity) < 1000000);

  // ── Compute the calPoints we'd save (depending on branch) ───────────────
  // Returns freshly-produced calibration points, or null if none were
  // entered this session (in which case an existing device cal is kept).
  const pendingCalPoints = useMemo(() => {
    if (restoredCalPoints) return restoredCalPoints;   // "Restore factory calibration"
    if (branch === 'factory' && codeValidation.ok) {
      return calPointsFromCodes(codeValidation.codeA, codeValidation.codeB);
    }
    if (branch === 'manual' && manualCalReady) {
      // Add source: 'manual', timestamp from each capture
      return manualCaptures.map(c => ({
        depthFt: c.depthFt,
        mA: c.mA,
        source: 'manual',
        ts: c.ts,
      }));
    }
    return null;
  }, [restoredCalPoints, branch, codeValidation, manualCalReady, manualCaptures]);

  // Calibration already on the device for this tank (factory or prior manual).
  const existingCalPoints = useMemo(() => {
    const c = deviceData?.[`tank${tank}`]?.calPoints;
    return (Array.isArray(c) && c.length >= 2) ? c : null;
  }, [deviceData, tank]);

  // What we'll actually USE: freshly entered points, else the device's
  // existing cal. `usingExistingCal` means we keep the device's cal untouched
  // and only save tank info.
  const effectiveCalPoints = pendingCalPoints || existingCalPoints;
  const usingExistingCal   = !pendingCalPoints && !!existingCalPoints;

  // Sensor cal preview — what depth does the live mA say, using the
  // user's just-entered cal? Drives the confirm page summary.
  const liveDepthWithNewCal = useMemo(() => {
    if (typeof liveMA !== 'number' || !pendingCalPoints) return null;
    return maToFeet(liveMA, pendingCalPoints);
  }, [liveMA, pendingCalPoints]);

  // Live tank % preview for step 4, using new cal + entered depths + live mA.
  const previewPct = useMemo(() => {
    if (!(emptyEntered && fullEntered) || typeof liveMA !== 'number' || !pendingCalPoints) return null;
    const r = regress(pendingCalPoints);
    if (!r) return null;
    const emptyMa = r.slope * emptyDepthFt + r.intercept;
    const fullMa  = r.slope * fullDepthFt  + r.intercept;
    if (Math.abs(fullMa - emptyMa) < 0.1) return null;
    return (liveMA - emptyMa) / (fullMa - emptyMa) * 100;
  }, [emptyEntered, fullEntered, liveMA, pendingCalPoints, emptyDepthFt, fullDepthFt]);

  // ── Manual cal capture handler ──────────────────────────────────────────
  //
  // Two-phase flow:
  //   Phase 1 — startCapture(depthFt) validates the depth, sets `capturing`,
  //             starts a 10s countdown.
  //   Phase 2 — countdown ends, finalizeCapture() runs, reads liveMA at
  //             *that moment* (not at click time), pairs with the typed
  //             depth, and pushes onto manualCaptures.
  //
  // The 10s window guarantees ≥1 fresh push lands during the wait (5s
  // cadence in fast mode), plus settle buffer for sensor motion. This
  // is the fix for the staleness bug seen in session 3E test, where
  // a fast-clicking user paired multiple distinct depths with the same
  // stale mA reading because click rate > push rate.
  //
  function startCapture(depthFt) {
    setPendingErr('');
    setPlausibilityErr('');

    if (typeof depthFt !== 'number' || !isFinite(depthFt) || depthFt < 0) {
      setPendingErr('Enter the current sensor depth.');
      return;
    }

    // Check for duplicate depth — same threshold as before.
    if (manualCaptures.some(c => Math.abs(c.depthFt - depthFt) < 0.05)) {
      setPendingErr('You already captured a reading at this depth. Try a different depth.');
      return;
    }

    if (manualCaptures.length >= MANUAL_CAL_MAX_POINTS) {
      setPendingErr(`Maximum ${MANUAL_CAL_MAX_POINTS} captures.`);
      return;
    }

    // Begin countdown. The actual capture happens in the timer callback below.
    setCapturing(true);
    setCaptureRemainingMs(CAPTURE_SETTLE_MS);

    const startedAt = Date.now();
    const tickId = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, CAPTURE_SETTLE_MS - elapsed);
      setCaptureRemainingMs(remaining);
      if (remaining <= 0) {
        clearInterval(tickId);
      }
    }, 100);  // 100ms tick keeps the countdown smooth without being expensive

    setTimeout(() => {
      clearInterval(tickId);
      finalizeCapture(depthFt);
    }, CAPTURE_SETTLE_MS);
  }

  // Phase 2 of the capture flow. Captures whatever liveMA is at this
  // moment. If the device has been disconnected during the countdown
  // and liveMA went null, the capture fails gracefully and the user
  // can retry.
  function finalizeCapture(depthFt) {
    setCapturing(false);
    setCaptureRemainingMs(0);

    if (typeof liveMA !== 'number' || !isFinite(liveMA)) {
      setPendingErr('No sensor reading received. Check the device is online and try again.');
      return;
    }

    setManualCaptures(prev => [
      ...prev,
      { depthFt, mA: liveMA, ts: Date.now() },
    ]);
    // Clear all three input states regardless of which mode was active.
    // Harmless to clear the ones that weren't in use.
    setPendingDepth('');
    setPendingFt('');
    setPendingIn('');

    // Brief "Captured ✓" flash before the button returns to idle.
    setJustCaptured(true);
    setTimeout(() => setJustCaptured(false), 1500);
  }

  function removeCapture(idx) {
    setManualCaptures(prev => prev.filter((_, i) => i !== idx));
    setPlausibilityErr('');
  }

  // ── Save ────────────────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true);
    setSaveError('');
    try {
      if (!effectiveCalPoints) {
        setSaveError('Sensor calibration is missing. Go back and complete it.');
        setSaving(false);
        return;
      }

      // Plausibility check only applies to freshly-entered points — an
      // existing (factory or prior) cal was already validated when it was
      // saved. Negative or near-zero slope means fresh captures don't fit
      // the physics of a 4-20 mA submersible sensor (usually a stale-reading
      // capture). Bounce back to step 3 with a specific message.
      if (pendingCalPoints) {
        const plausibility = validateRegressionPlausibility(pendingCalPoints);
        if (!plausibility.ok) {
          console.warn('[wizard] regression failed plausibility:', plausibility);
          setPlausibilityErr(plausibility.reason);
          setStep(3);
          setSaving(false);
          return;
        }
      }

      // Build the patch using dot-notation to preserve other tank fields.
      // Only WRITE calPoints when the user produced new ones — when keeping
      // the device's existing cal we leave it untouched.
      const tk = `tank${tank}`;
      const patch = {};
      if (pendingCalPoints) {
        patch[`${tk}.calPoints`] = pendingCalPoints;
      }

      // Tank info (feet) — write whatever the user entered, blank → null.
      // Entered-flag check (not > 0) so an explicit 0 ft 0 in saves as 0.
      const hasEmpty = emptyEntered;
      const hasFull  = fullEntered;
      patch[`${tk}.emptyFt`] = hasEmpty ? emptyDepthFt : null;
      patch[`${tk}.fullFt`]  = hasFull  ? fullDepthFt  : null;

      // Derive mA values from feet × new calPoints (pair-sync Rule 1).
      // If feet are blank, mA stays null.
      const derived = deriveMaFromCalChange(
        effectiveCalPoints,
        hasEmpty ? emptyDepthFt : null,
        hasFull  ? fullDepthFt  : null
      );
      patch[`${tk}.emptyMa`] = derived.emptyMa;
      patch[`${tk}.fullMa`]  = derived.fullMa;

      // Capacity (optional)
      if (capacity) {
        patch[`${tk}.capacity`] = parseFloat(capacity);
      }

      // Fast mode so the device picks up new mA values within ~15s
      patch.fastModeUntil = Timestamp.fromMillis(Date.now() + FAST_MODE_SAVE_MS);

      await updateDoc(doc(db, 'devices', deviceId), patch);

      // Toast cue, navigate back. First-time setup ends on activating the
      // included year of Plus; /plus passes the toast on to the dashboard.
      navigate(onboarding ? `/plus/${deviceId}` : returnTo, {
        state: { toast: 'Saved. Your device will update within 60 seconds.' }
      });
    } catch (err) {
      console.error('Wizard save error:', err);
      setSaveError("Couldn't save. Check your connection and try again.");
      setSaving(false);
    }
  }

  function handleCancel() {
    // If user has entered anything substantial, confirm before discarding.
    const dirty =
      branch !== null ||
      codeA || codeB ||
      manualCaptures.length > 0 ||
      emptyEntered || fullEntered ||
      capacity;
    if (dirty && !window.confirm('Discard your changes and exit setup?')) return;
    navigate(returnTo);
  }

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="app-container">
        <TopBar subtitle="Set up your tank" />
        <div className="loading-page"><div className="spinner" /></div>
      </div>
    );
  }

  // Tank name from device doc — fallbacks to default
  const tankName = (() => {
    const t = deviceData?.[`tank${tank}`];
    return (t?.name) || (tank === 1 ? 'Tank 1' : 'Tank 2');
  })();


  const locationName = deviceData?.name || 'Your location';
  const wizPositions = factorySkip ? [4, 5] : [1, branch === 'manual' ? 3 : 2, 4, 5];
  const curIdx = wizPositions.indexOf(step);
  // Already-calibrated check for the recalibrate warning on step 1.
  const alreadyCalibrated = !!existingCalPoints;
  const currentCalSource  = deviceData?.[`tank${tank}`]?.calPoints?.[0]?.source;

  // Factory-cal archive for this tank (written at provisioning / by the
  // cloud). Present + current cal isn't already factory → offer restore.
  const factoryArchivePts = (() => {
    const pts = deviceData?.factoryCal?.[`tank${tank}`]?.points;
    return (Array.isArray(pts) && pts.length >= 2) ? pts : null;
  })();
  const canRestoreFactory = !!factoryArchivePts && currentCalSource !== 'factory';

  // Recalibrate-screen actions: keep the current cal (just edit tank info),
  // or restore the archived factory cal. Both jump to the tank-info step.
  function keepCurrentCal() {
    setRestoredCalPoints(null);
    setFactorySkip(true);
    setSaveError('');
    setStep(4);
  }
  function restoreFactoryCal() {
    if (!factoryArchivePts) return;
    setRestoredCalPoints(factoryArchivePts);
    setFactorySkip(true);
    setSaveError('');
    setStep(4);
  }

  // One-line calibration summary for the confirm step.
  const calSummary =
    restoredCalPoints ? 'Factory calibration (restored)' :
    usingExistingCal
      ? (currentCalSource === 'factory' ? 'Factory calibrated' : 'Current calibration kept')
      : branch === 'factory'
        ? `Tag codes: A=${codeValidation.ok ? codeValidation.codeA : ''}, B=${codeValidation.ok ? codeValidation.codeB : ''}`
        : `Manual (${manualCaptures.length} points)`;

  const wizTitle = recalibrate ? 'Recalibrate' : 'Tank setup';
  const wizSub = factorySkip
    ? "Just confirm your tank's details below."
    : "We'll walk through it together. About 5 minutes.";

  return (
    <div className="app-container wiz-app">
      <div className="page-content wiz-page">

        <div className="wiz-header">
          <div className="wiz-header__eyebrow">{tankName} · {locationName}</div>
          <div className="wiz-header__row">
            <h1 className="wiz-header__title">{wizTitle}</h1>
            <button type="button" className="wiz-cancel" onClick={handleCancel}>Cancel</button>
          </div>
          <p className="wiz-header__sub">{wizSub}</p>
        </div>

        {/* Device mid-update: live sensor readings pause until it finishes. */}
        {isFirmwareUpdating(deviceData?.fw) && (
          <div className="alert-banner info" style={{ marginBottom: 12 }}>
            <div className="alert-banner-title">Device is updating</div>
            <div className="alert-banner-desc">
              Live calibration will be ready in a moment — you can enter your tank details now.
            </div>
          </div>
        )}

        {/* Stepper */}
        {step >= 1 && (
          <div className="wiz-steps">
            {wizPositions.map((sPos, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className={`wiz-steps__bar ${i <= curIdx ? 'is-done' : ''}`} />}
                <div className={`wiz-steps__node ${i < curIdx ? 'is-done' : i === curIdx ? 'is-current' : ''}`}>
                  {i < curIdx ? <WizCheck /> : (i + 1)}
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step 0 — Tank picker */}
        {step === 0 && (
          <div className="card wiz-card">
            <h3 className="wiz-card__title">Which tank are you setting up?</h3>
            <p className="wiz-card__lead">
              Your device has two tanks connected. Choose the one to set up first —
              you can come back and do the other afterward.
            </p>
            <button
              type="button"
              className="btn btn-primary wiz-tank-pick"
              onClick={() => { setTank(1); setStep(1); }}
            >
              {deviceData?.tank1?.name || 'Tank 1'}
            </button>
            <button
              type="button"
              className="btn btn-secondary wiz-tank-pick"
              onClick={() => { setTank(2); setStep(1); }}
            >
              {deviceData?.tank2?.name || 'Tank 2'}
            </button>
          </div>
        )}

        {/* Step 1 — Method picker */}
        {step === 1 && (
          <div className="card wiz-card">
            <h3 className="wiz-card__title">How would you like to set up the sensor?</h3>
            <p className="wiz-card__lead">There are two ways. Use the sensor tag if you have it — it's faster.</p>
            <div className="wiz-card__divider" />

            {alreadyCalibrated && !onboarding && (
              <>
                <div className="wiz-note wiz-note--warn">
                  <span className="wiz-note__icon"><WizHelp /></span>
                  <div><strong>This tank is already calibrated.</strong> You usually don't need to redo this — only recalibrate if readings seem off, or you moved or replaced the sensor.</div>
                </div>
                <button type="button" className="btn btn-primary wiz-tank-pick" onClick={keepCurrentCal}>
                  Keep current calibration
                </button>
                {canRestoreFactory && (
                  <button type="button" className="btn btn-secondary wiz-tank-pick" onClick={restoreFactoryCal}>
                    Restore factory calibration
                  </button>
                )}
                <div className="wiz-card__divider" />
                <p className="wiz-card__lead" style={{ marginTop: 0 }}>Or set the sensor up again:</p>
              </>
            )}

            <button type="button" className="wiz-option" onClick={() => { setBranch('factory'); setStep(2); }}>
              <span className="wiz-option__icon"><WizBolt /></span>
              <span className="wiz-option__body">
                <span className="wiz-option__title">Use the tag from the sensor <span className="wiz-option__badge">Recommended</span></span>
                <span className="wiz-option__desc">Two numbers printed on a tag attached to the cable. Takes a minute.</span>
              </span>
              <span className="wiz-option__chev"><WizChevron /></span>
            </button>

            <button type="button" className="wiz-option wiz-option--moss" onClick={() => { setBranch('manual'); setStep(3); }}>
              <span className="wiz-option__icon"><WizDrop /></span>
              <span className="wiz-option__body">
                <span className="wiz-option__title">Calibrate by hand</span>
                <span className="wiz-option__desc">Lower the sensor to depths you measure yourself. Takes 10 minutes.</span>
              </span>
              <span className="wiz-option__chev"><WizChevron /></span>
            </button>

            <div className="wiz-note">
              <span className="wiz-note__icon"><WizHelp /></span>
              <div><strong>Lost the tag?</strong> No problem — calibrate by hand. It's just as accurate, and can fine-tune readings if they ever seem off.</div>
            </div>
          </div>
        )}

        {/* Step 2 — Factory tag entry (Branch A) */}
        {step === 2 && branch === 'factory' && (
          <div className="card wiz-card">
            <h3 className="wiz-card__title">Type in the two codes from the tag</h3>
            <p className="wiz-card__lead">The tag is a small plastic strip attached to the sensor cable. The codes are 3–5 digits each.</p>

            <div className="wiz-tag-illo">
              <div className="wiz-tag">
                <div className="wiz-tag__head">SOKO SENSOR · TAG</div>
                <div className="wiz-tag__line">A: 4477</div>
                <div className="wiz-tag__line">B: 7908</div>
              </div>
            </div>

            <div className="field">
              <label htmlFor="wiz-codeA">Code A</label>
              <input
                id="wiz-codeA"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="e.g. 4477"
                value={codeA}
                onChange={e => { setCodeA(e.target.value); setCodeError(''); }}
                autoComplete="off"
              />
              <div className="hint">The first number on the tag.</div>
            </div>

            <div className="field">
              <label htmlFor="wiz-codeB">Code B</label>
              <input
                id="wiz-codeB"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="e.g. 7908"
                value={codeB}
                onChange={e => { setCodeB(e.target.value); setCodeError(''); }}
                autoComplete="off"
              />
              <div className="hint">The second number on the tag.</div>
            </div>

            {(codeA || codeB) && !codeValidation.ok && (
              <div className="wiz-hint wiz-hint--warn">
                {codeValidation.error}
              </div>
            )}

            {codeValidation.ok && (
              <div className="wiz-hint">
                ✓ Codes look good: A = {codeValidation.codeA}, B = {codeValidation.codeB}
              </div>
            )}

            <div className="wiz-actions">
              <button type="button" className="wiz-back" onClick={() => { setBranch(null); setStep(1); }}>
                ← Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!codeValidation.ok}
                onClick={() => setStep(4)}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Manual cal capture (Branch B) */}
        {step === 3 && branch === 'manual' && (
          <div className="card wiz-card">
            <h3 className="wiz-card__title">Manual calibration</h3>
            <p className="wiz-card__lead">
              Lower the sensor on its cable to a known depth in the water,
              type that depth below, and press <strong>Capture</strong>.
              Repeat at two or more depths.
            </p>

            <p className="wiz-card__lead">
              Before you start, give the button on your device a quick tap
              (under one second). This wakes its internet connection so
              readings update quickly.
            </p>

            <p className="wiz-card__lead">
              <em>Tips: two readings are enough; up to four improves accuracy.
              Depths spaced about a foot apart work best.</em>
            </p>

            {/* Live sensor reading — shows the user what the device is seeing right now,
                with age in seconds + amber styling when stale. Replaces "trust me" UX
                where the user had no visibility into whether the reading was fresh. */}
            <div className={`wiz-live-reading ${readingFresh ? 'wiz-live-reading--fresh' : 'wiz-live-reading--stale'}`}>
              {typeof liveMA === 'number' && readingAgeMs != null ? (
                <>
                  Raw sensor reading: <strong>{liveMA.toFixed(2)} mA</strong>
                  {' · '}
                  {readingAgeMs < 1000
                    ? 'just now'
                    : `${Math.round(readingAgeMs / 1000)}s ago`}
                  {!readingFresh && ' — waiting for refresh…'}
                </>
              ) : (
                <>Raw sensor reading: — · waiting for first push…</>
              )}
            </div>
            {/* A monitor checking in slowly (Free reports hourly) only hears
                about fast mode on its next check-in. A tap on its button makes
                it connect now and report every 10 s for 20 minutes. */}
            {!readingFresh && (
              <p className="wiz-tap-hint">
                Not updating? Give the button on your monitor a short press. It connects right away
                and sends a reading every 10 seconds while you set up.
              </p>
            )}

            {/* Depth input — reuses the DepthInput helper used by step 4,
                so layout and format-matching is identical (paired ft/in
                fields for ft-in mode, single field otherwise). Clears
                pendingErr on any input change. */}
            <DepthInput
              format={prefs.depthFormat}
              label="Current sensor depth"
              decimalValue={pendingDepth}
              onDecimalChange={v => { setPendingDepth(v); setPendingErr(''); }}
              ftValue={pendingFt}
              onFtChange={v => { setPendingFt(v); setPendingErr(''); }}
              inValue={pendingIn}
              onInChange={v => { setPendingIn(v); setPendingErr(''); }}
            />

            {pendingErr && (
              <div className="wiz-hint wiz-hint--warn">{pendingErr}</div>
            )}

            {/* Plausibility error from Continue or Save attempt. Provides the
                slope-failure recovery guidance. */}
            {plausibilityErr && (
              <div className="wiz-hint wiz-hint--warn" style={{ marginTop: 12 }}>
                <strong>The captures don't form a sensible pattern.</strong>
                <br /><br />
                As the sensor goes deeper, the readings should rise — but yours
                don't (or barely do). This usually means:
                <ul style={{ marginTop: 8, marginBottom: 8 }}>
                  <li><strong>The sensor wasn't actually at the depth you typed</strong> for one or more captures, or</li>
                  <li><strong>The reading hadn't refreshed yet</strong> when you pressed Capture.</li>
                </ul>
                Try the captures again. Make sure the sensor has settled at each
                depth, wait for the timer to finish before moving the sensor, and
                double-check the depth you type matches where the sensor actually is.
                <br /><br />
                <em>Remove individual captures with Remove next to each one to
                retry, or click Back to start over. If these issues persist,
                please contact customer support.</em>
              </div>
            )}

            <div className="wiz-actions">
              <button
                type="button"
                className={`btn ${justCaptured ? 'btn-success' : 'btn-primary'} wiz-capture-btn`}
                onClick={() => {
                  // Parse depth from whichever input mode is active.
                  let depthFt;
                  if (prefs.depthFormat === 'ft-in') {
                    const ft = parseFloat(pendingFt) || 0;
                    const inches = parseFloat(pendingIn) || 0;
                    depthFt = ft + (inches / 12);
                  } else if (prefs.depthFormat === 'inches') {
                    const raw = parseFloat(pendingDepth);
                    if (isNaN(raw) || raw < 0) {
                      setPendingErr('Enter the current sensor depth.');
                      return;
                    }
                    depthFt = raw / 12;
                  } else {
                    const raw = parseFloat(pendingDepth);
                    if (isNaN(raw) || raw < 0) {
                      setPendingErr('Enter the current sensor depth.');
                      return;
                    }
                    depthFt = raw;
                  }
                  startCapture(depthFt);
                }}
                disabled={
                  capturing ||
                  justCaptured ||
                  (prefs.depthFormat === 'ft-in'
                    ? !pendingFt && !pendingIn
                    : !pendingDepth)
                }
              >
                {capturing
                  ? `Reading… ${Math.ceil(captureRemainingMs / 1000)}s`
                  : justCaptured
                    ? 'Captured ✓'
                    : 'Capture reading'}
              </button>
            </div>

            {/* Helper text under the button — different message per state */}
            {capturing && (
              <div className="wiz-hint">Hold the sensor steady — capturing a reading.</div>
            )}
            {justCaptured && (
              <div className="wiz-hint">Reading captured. Move to the next depth when ready.</div>
            )}
            {!capturing && !justCaptured && (prefs.depthFormat === 'ft-in' ? (pendingFt || pendingIn) : pendingDepth) && (
              <div className="wiz-hint">Hold the sensor still at the depth you entered, then capture.</div>
            )}

            {/* List of captures — depth only, no mA shown per UX policy */}
            {manualCaptures.length > 0 && (
              <>
                <div className="settings-subsection-header" style={{ marginTop: 20 }}>
                  <div className="settings-subsection-title">
                    Captured ({manualCaptures.length}/{MANUAL_CAL_MAX_POINTS})
                  </div>
                </div>
                <div className="wiz-summary">
                  {manualCaptures.map((c, idx) => (
                    <div key={idx} className="wiz-summary__line">
                      <span>At {formatDepth(c.depthFt, prefs.depthFormat)}</span>
                      <button
                        type="button"
                        className="wiz-reset-link"
                        onClick={() => removeCapture(idx)}
                        style={{ marginTop: 0 }}
                        disabled={capturing}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                {manualCaptures.length < MANUAL_CAL_MIN_POINTS && (
                  <div className="wiz-hint">
                    Capture at least {MANUAL_CAL_MIN_POINTS} different depths to continue.
                  </div>
                )}
                {manualCaptures.length >= MANUAL_CAL_MIN_POINTS && !manualCalReady && (
                  <div className="wiz-hint wiz-hint--warn">
                    Your depths are too close together. Add a capture at least
                    {' '}{MANUAL_CAL_MIN_SPAN_FT} ft from your others.
                  </div>
                )}
              </>
            )}

            <div className="wiz-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setBranch(null); setStep(1); }}
                disabled={capturing}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!manualCalReady || capturing}
                onClick={() => {
                  // Plausibility check before advancing — catches bad data
                  // here rather than on Save where it's a worse UX.
                  const plausibility = validateRegressionPlausibility(pendingCalPoints);
                  if (!plausibility.ok) {
                    console.warn('[wizard] regression failed plausibility:', plausibility);
                    setPlausibilityErr(plausibility.reason);
                    return;
                  }
                  setPlausibilityErr('');
                  setStep(4);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 4 — Water levels (empty/full depth, capacity) */}
        {step === 4 && (
          <div className="card wiz-card">
            <h3 className="wiz-card__title">What counts as empty and what counts as full?</h3>
            <p className="wiz-card__lead">Empty is usually the height of your pickup tube — the lowest water you can actually use. Full is the overflow level.</p>

            {factorySkip && (
              <div className="wiz-note">
                <span className="wiz-note__icon"><WizHelp /></span>
                <div><strong>Your sensor is already calibrated.</strong> We just need your tank's empty and full levels below. You can recalibrate anytime from Settings.</div>
              </div>
            )}

            {previewPct !== null && (
              <div className="wiz-preview">
                <div className="wiz-preview__vessel">
                  <div className="wiz-preview__fill" style={{ height: `${Math.max(0, Math.min(100, previewPct))}%` }} />
                </div>
                <div>
                  <div className="wiz-preview__pct">{Math.round(previewPct)}%</div>
                  <div className="wiz-preview__note">This is what your dashboard will show right now, using these settings.</div>
                </div>
              </div>
            )}

            <DepthInput
              format={prefs.depthFormat}
              label="Empty depth — where 0% sits"
              decimalValue={emptyDepth}
              onDecimalChange={setEmptyDepth}
              ftValue={emptyFt}
              onFtChange={setEmptyFt}
              inValue={emptyIn}
              onInChange={setEmptyIn}
            />

            <DepthInput
              format={prefs.depthFormat}
              label="Full depth — where 100% sits"
              decimalValue={fullDepth}
              onDecimalChange={setFullDepth}
              ftValue={fullFt}
              onFtChange={setFullFt}
              inValue={fullIn}
              onInChange={setFullIn}
            />

            {!tankInfoValidation.ok && (emptyEntered || fullEntered) && (
              <div className="wiz-hint wiz-hint--warn">
                {tankInfoValidation.error}
              </div>
            )}

            {tankInfoValidation.ok && tankInfoValidation.warns.length > 0 && (
              <div className="wiz-hint">
                {tankInfoValidation.warns.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}

            <div className="field">
              <label htmlFor="wiz-capacity">Capacity in gallons (optional)</label>
              <input
                id="wiz-capacity"
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 1500"
                value={capacity}
                onChange={e => setCapacity(e.target.value)}
              />
              <div className="hint">If you know it, we'll show gallons too — otherwise just %.</div>
            </div>

            <div className="wiz-actions">
              {factorySkip ? <span /> : (
                <button
                  type="button"
                  className="wiz-back"
                  onClick={() => setStep(branch === 'manual' ? 3 : 2)}
                >
                  ← Back
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                disabled={!tankInfoValidation.ok || !capacityValid}
                onClick={() => setStep(5)}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* Step 5 — Confirm + save */}
        {step === 5 && (
          <ConfirmStep
            tankName={tankName}
            calSummary={calSummary}
            emptyDepthFt={emptyDepthFt}
            fullDepthFt={fullDepthFt}
            emptyEntered={emptyEntered}
            fullEntered={fullEntered}
            capacity={capacity}
            liveDepthWithNewCal={liveDepthWithNewCal}
            effectiveCalPoints={effectiveCalPoints}
            liveMA={liveMA}
            depthFormat={prefs.depthFormat}
            saving={saving}
            saveError={saveError}
            onBack={() => setStep(4)}
            onSave={handleSave}
          />
        )}

      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Helpers
// ════════════════════════════════════════════════════════════════════════════

// Parse depth input fields into canonical decimal-feet, respecting format.
function parseInputDepth(format, decimalStr, ftStr, inStr) {
  if (format === 'ft-in') {
    const ft = parseFloat(ftStr) || 0;
    const i  = parseFloat(inStr) || 0;
    return ft + i / 12;
  }
  if (format === 'inches') {
    return (parseFloat(decimalStr) || 0) / 12;
  }
  return parseFloat(decimalStr) || 0;
}

// ════════════════════════════════════════════════════════════════════════════
//  DepthInput — adapts to user's depth format pref
// ════════════════════════════════════════════════════════════════════════════
function DepthInput({
  format, label,
  decimalValue, onDecimalChange,
  ftValue, onFtChange,
  inValue, onInChange,
}) {
  if (format === 'ft-in') {
    return (
      <div className="field">
        <label>{label}</label>
        <div className="wiz-depth-row">
          <div className="field" style={{ marginBottom: 0 }}>
            <input
              type="number"
              min="0"
              step="1"
              placeholder="0"
              value={ftValue}
              onChange={e => onFtChange(e.target.value)}
              aria-label="Feet"
            />
            <div className="hint">feet</div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <input
              type="number"
              min="0"
              max="11"
              step="1"
              placeholder="0"
              value={inValue}
              onChange={e => onInChange(e.target.value)}
              aria-label="Inches"
            />
            <div className="hint">inches</div>
          </div>
        </div>
      </div>
    );
  }
  if (format === 'inches') {
    return (
      <div className="field">
        <label>{label}</label>
        <input
          type="number"
          min="0"
          step="1"
          placeholder="0"
          value={decimalValue}
          onChange={e => onDecimalChange(e.target.value)}
        />
        <div className="hint">inches</div>
      </div>
    );
  }
  // ft-decimal
  return (
    <div className="field">
      <label>{label}</label>
      <input
        type="number"
        min="0"
        step="0.1"
        placeholder="0.0"
        value={decimalValue}
        onChange={e => onDecimalChange(e.target.value)}
      />
      <div className="hint">feet</div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  ConfirmStep — final preview before save
// ════════════════════════════════════════════════════════════════════════════
function ConfirmStep({
  tankName, calSummary,
  emptyDepthFt, fullDepthFt, emptyEntered, fullEntered, capacity,
  liveDepthWithNewCal, effectiveCalPoints, liveMA,
  depthFormat,
  saving, saveError, onBack, onSave,
}) {
  const hasEmpty = emptyEntered;
  const hasFull  = fullEntered;
  const capNum   = capacity ? parseFloat(capacity) : 0;

  // Projected percent at the live reading, using the cal we'll use + tank info
  let projectedPct = null;
  if (hasEmpty && hasFull && typeof liveMA === 'number' && effectiveCalPoints) {
    const r = regress(effectiveCalPoints);
    if (r) {
      const emptyMa = r.slope * emptyDepthFt + r.intercept;
      const fullMa  = r.slope * fullDepthFt  + r.intercept;
      if (Math.abs(fullMa - emptyMa) > 0.1) {
        projectedPct = (liveMA - emptyMa) / (fullMa - emptyMa) * 100;
      }
    }
  }

  const projectedGal = (projectedPct !== null && capNum > 0)
    ? Math.max(0, Math.round(projectedPct / 100 * capNum))
    : null;

  const wonky = projectedPct !== null && (projectedPct < -10 || projectedPct > 110);

  return (
    <div className="card wiz-card">
      <h3 className="wiz-card__title">Quick look before you save</h3>
      <p className="wiz-card__lead">Make sure everything looks right — you can always come back and adjust later.</p>

      {/* What sensor cal we'll save */}
      <div className="wiz-summary">
        <div className="wiz-summary__line">
          <span>Sensor calibration</span>
          <strong>{calSummary}</strong>
        </div>

        {hasEmpty && (
          <div className="wiz-summary__line">
            <span>Empty depth</span>
            <strong>{formatDepth(emptyDepthFt, depthFormat)}</strong>
          </div>
        )}
        {hasFull && (
          <div className="wiz-summary__line">
            <span>Full depth</span>
            <strong>{formatDepth(fullDepthFt, depthFormat)}</strong>
          </div>
        )}
        {!hasEmpty && !hasFull && (
          <div className="wiz-summary__line">
            <span>Tank dimensions</span>
            <strong>Not set — sensor reading only</strong>
          </div>
        )}
        {capNum > 0 && (
          <div className="wiz-summary__line">
            <span>Capacity</span>
            <strong>{capNum.toLocaleString()} gal</strong>
          </div>
        )}

      </div>

      {projectedPct !== null && (
        <div className={`wiz-result ${wonky ? 'wiz-result--warn' : ''}`}>
          <span className="wiz-result__icon">{wonky ? <WizWarn /> : <WizCheck />}</span>
          <div>
            <strong>Sensor reads {Math.round(projectedPct)}% full right now</strong>
            {projectedGal !== null && `. That's about ${projectedGal.toLocaleString()} gallons.`}
            {wonky
              ? ' This looks off — go back and check the depths or sensor placement.'
              : ' If this number looks way off, go back and check the depths.'}
          </div>
        </div>
      )}

      {saveError && (
        <div className="alert-banner fault">
          <div className="alert-banner-desc">{saveError}</div>
        </div>
      )}

      <div className="wiz-actions">
        <button
          type="button"
          className="wiz-back"
          onClick={onBack}
          disabled={saving}
        >
          ← Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save and finish'}
        </button>
      </div>
    </div>
  );
}
